// Alert — inline message banner. Four variants keyed to intent:
// info (neutral), success, warning, error. Uses role="status" for
// non-error variants so screen readers announce politely, and
// role="alert" for `error` so they announce immediately.

import type { ReactNode } from 'react'

export type AlertVariant = 'info' | 'success' | 'warning' | 'error'

export interface AlertProps {
  variant?: AlertVariant
  title?: string
  children?: ReactNode
}

const VARIANT_CLASSES: Record<AlertVariant, string> = {
  info: 'border-blue-200 bg-blue-50 text-blue-900',
  success: 'border-green-200 bg-green-50 text-green-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  error: 'border-red-200 bg-red-50 text-red-900',
}

export function Alert({ variant = 'info', title, children }: AlertProps) {
  const role = variant === 'error' ? 'alert' : 'status'
  return (
    <div
      role={role}
      className={[
        'rounded-md border px-4 py-3 text-sm',
        VARIANT_CLASSES[variant],
      ].join(' ')}
    >
      {title !== undefined ? (
        <p className="font-medium">{title}</p>
      ) : null}
      {children !== undefined ? <div className="mt-1">{children}</div> : null}
    </div>
  )
}
